import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal, Alert } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import Helpers from 'utils/helpers';
import { createTimeout, triggerTimeoutEarly } from 'utils/timeout';
import './popup.css';

function ModalHeader(props) {
  return (
    <div className='modal-header bg-app text-white d-flex justify-content-between align-items-center w-100'>
      <div className='modal-header-left-icon'>
        {props.leftIconClass ? <i className={props.leftIconClass} onClick={props.onLeftIconClick} /> : null}
      </div>
      <div className='modal-title'>{props.title}</div>
      <div className='modal-header-right-icon'>
        {props.onClose ? <i className='icons8-delete' onClick={props.onClose} /> : null}
      </div>
    </div>
  );
}

export default function Popup(props) {
  const size = props.size || 'md'; //sm, md (default), lg, xl
  const position = props.position || 'center'; //center (default), fullscreen, topscreen

  const getClass = () => {
    let classNames = [props.className];
    if (position === 'fullscreen') {
      classNames.push('modal-fullscreen');
    }
    else if (position === 'topscreen') {
      classNames.push('modal-topscreen');
    }
    return Helpers.String.join(classNames);
  };

  return (
    <Modal show={props.show} onHide={props.onClose} className={getClass()} size={size} centered={position === 'center'}>
      <ModalHeader title={props.title} onClose={props.onClose} leftIconClass={props.leftIconClass} onLeftIconClick={props.onLeftIconClick} />
      <Modal.Body>{props.content ? props.content : props.children}</Modal.Body>
      {!props.footer ? null : <Modal.Footer>{props.footer}</Modal.Footer>}
    </Modal>
  );
}

export function ConfirmPopup(props) {
  const { message, size, declineVariant, declineLabel, declineIconClass, onDecline, acceptVariant, acceptLabel, acceptIconClass, onAccept, ...popupProps } = props;

  const handleAccept = () => {
    props.onClose();
    if (props.onAccept) {
      props.onAccept();
    }
  };

  const handleDecline = () => {
    props.onClose();
    if (props.onDecline) {
      props.onDecline();
    }
  };

  const renderContent = () => {
    if (message) {
      return <div>{props.message}</div>;
    }
    return props.children;
  };

  const renderFooter = () => {
    return (
      <>
        <Inputs.Button variant={declineVariant || 'default'} onClick={handleDecline} label={declineLabel || 'No'} iconClass={declineIconClass} />
        <Inputs.Button variant={acceptVariant || 'app'} onClick={handleAccept} label={acceptLabel || 'Yes'} iconClass={acceptIconClass} />
      </>
    );
  };

  return (
    <Popup {...popupProps} content={renderContent()} footer={renderFooter()} size={size || 'sm'} />
  );
}

export function ScrollingPopup(props) {
  const getBodyClass = () => {
    let classNames = ['modal-fullscreen-scroll'];
    if (props.header) {
      classNames.push('with-header');
    }
    if (props.footer) {
      classNames.push('with-footer');
    }
    return Helpers.String.join(classNames);
  };

  return (
    <Modal show={props.show} onHide={props.onClose} className={Helpers.String.join(['modal-fullscreen', props.className])}>
      <ModalHeader title={props.title} onClose={props.onClose} leftIconClass={props.leftIconClass} onLeftIconClick={props.onLeftIconClick} />
      {!props.header ? null : <Modal.Footer className='modal-fullscreen-header justify-content-start'>{props.header}</Modal.Footer>}
      <Modal.Body className={getBodyClass()}>{props.content ? props.content : props.children}</Modal.Body>
      {!props.footer ? null : <Modal.Footer className='modal-fullscreen-footer'>{props.footer}</Modal.Footer>}
    </Modal>
  );
}

const NotifyPopupManager = {
  _timeouts: [],
  add: function (timeoutId) {
    this._timeouts.push(timeoutId);
  },
  hideAll: function () {
    for (let i = 0; i < this._timeouts.length; i++) {
      triggerTimeoutEarly(this._timeouts.length);
    }
  },
};

export function NotifyPopup(props) {
  const [show, setShow] = useState(true);

  const handleClose = () => {
    setShow(false);
    if (props.onClose) {
      props.onClose();
    }
  };

  const getTitle = () => {
    if (props.title) {
      let icon = null;
      switch (props.variant) {
        case 'success':
          icon = <i className='icons8-checkmark mr-1' />;
          break;
        case 'danger':
          icon = <i className='icons8-delete-shield mr-1' />;
          break;
        case 'warning':
          icon = <i className='icons8-error mr-1' />;
          break;
        case 'info':
          icon = <i className='icons8-about mr-1' />;
          break;
        default:
          break;
      }
      return <Alert.Heading>{icon}{props.title}</Alert.Heading>;
    }
    return null;
  };

  const getClass = () => {
    let classNames = ['notify', props.className];
    if (props.yPosition) {
      classNames.push(`notify-y-${props.yPosition}`);
    }
    else {
      classNames.push('notify-y-bottom');
    }
    if (props.xPosition) {
      classNames.push(`notify-x-${props.xPosition}`);
    }
    else {
      classNames.push('notify-y-center');
    }
    return Helpers.String.join(classNames);
  };

  if (show === true) {
    if (props.seconds && props.seconds > 0) {
      NotifyPopupManager.hideAll();
      NotifyPopupManager.add(createTimeout(handleClose, 1000 * props.seconds));
    }
    return (
      <Alert show={show} className={getClass()} variant={props.variant} dismissible={props.showClose} onClose={handleClose}>
        {getTitle()}
        {props.message ? <div>{props.message}</div> : props.children}
      </Alert>
    );
  }
  else {
    return null;
  }
}

export function MediaPopup(props) {
  const handleClose = () => {
    if (props.onClose) {
      props.onClose();
    }
  };

  if (props.show === true) {
    return (
      <div className='mediamodal' onClose={handleClose}>
        <div className='mediamodal-header bg-app text-white d-flex justify-content-between align-items-center w-100'>
          <div className='mediamodal-header-left-icon'></div>
          <div className='mediamodal-title'>{props.title}</div>
          <div className='mediamodal-header-right-icon'>
            <i className='icons8-delete' onClick={props.onClose} />
          </div>
        </div>
        <div className='mediamodal-body'>{props.content ? props.content : props.children}</div>
      </div>
    );
  }
  else {
    return null;
  }
}

export const mount = (component, props, container, callback) => {
  var reactElement = React.createElement(component, props, null);
  ReactDOM.render(reactElement, container, callback);
  return reactElement;
};

export const unmount = (container) => {
  ReactDOM.unmountComponentAtNode(container);
};

const show = (component, container) => {
  if (container && container.appendChild) {
    container.appendChild(component);
  }
};

const hide = (component) => {
  if (component && component.remove) {
    component.remove();
  }
};

/* NON-REACT, PURE JS FUNCTIONS USED TO DISPLAY POPUPS */

export function ShowConfirmPopup({ title, message, acceptLabel, acceptIconClass, acceptVariant, declineLabel, declineIconClass, declineVariant, onAccept, onDecline, onClose }) {
  const div = document.createElement('div');
  mount(
    ConfirmPopup,
    {
      title: title || 'Confirm',
      message: message || 'Are you sure?',
      show: true,
      acceptLabel: acceptLabel,
      acceptIconClass: acceptIconClass,
      acceptVariant: acceptVariant || 'app',
      declineLabel: declineLabel,
      declineIconClass: declineIconClass,
      declineVariant: declineVariant || 'default',
      onAccept: onAccept,
      onDecline: onDecline,
      onClose: () => {
        unmount(div);
        if (onClose) {
          onClose();
        }
      }
    },
    div
  );
}

export function ShowNotifyPopup({ title, message, variant, yPosition, xPosition, seconds, showClose, onClose }) {
  const div = document.createElement('div');
  mount(
    NotifyPopup,
    {
      title: title || '',
      message: message || '',
      show: true,
      variant: variant || '',
      yPosition: yPosition || 'bottom',
      xPosition: xPosition || 'left',
      seconds: seconds || -1,
      showClose: showClose,
      onClose: () => {
        hide(div);
        unmount(div);
        if (onClose) {
          onClose();
        }
      }
    },
    div
  );
  show(div, document.body);
}

export function ShowScrollingPopup({ title, content, header, footer, leftIconClass, onLeftIconClick, onClose }) {
  const div = document.createElement('div');
  mount(
    ScrollingPopup,
    {
      title: title || '',
      content: content || '',
      show: true,
      header: header,
      footer: footer,
      leftIconClass: leftIconClass,
      onLeftIconClick: onLeftIconClick,
      onClose: () => {
        unmount(div);
        if (onClose) {
          onClose();
        }
      }
    },
    div
  );
}

export function ShowMediaPopup({ title, content, onClose }) {
  const div = document.createElement('div');
  mount(
    MediaPopup,
    {
      title: title || '',
      content: content || '',
      show: true,
      onClose: () => {
        hide(div);
        unmount(div);
        if (onClose) {
          onClose();
        }
      }
    },
    div
  );
  show(div, document.body);
}